import { keepPreviousData, useQueries } from '@tanstack/react-query';

import { apiMutator, isAppError } from '@cenorion/api';

import { usePermission } from '@/core/authz/use-permission';

import {
  toConfigurationResult,
  toDeviceResult,
  toGroup,
  toPlanResult,
  toWorkflowResult,
} from '../services/search-adapter';
import { SEARCH_GROUP_LIMIT, SEARCH_GROUPS, SEARCH_MIN_LENGTH } from '../types';

import type {
  ConfigurationSearchItem,
  DeviceSearchItem,
  PlanSearchItem,
  SearchBody,
  WorkflowSearchItem,
} from '../services/search-contract';
import type { SearchGroupType, SearchResultGroup } from '../types';
import type { Permission } from '@/core/authz/permissions';

type GroupSource = {
  /** The route's own permission: a user without it is never sent there. */
  permission: Permission;
  fetch: (q: string, signal: AbortSignal) => Promise<SearchResultGroup>;
};

/**
 * `GET /v1/search/{route}` through the shared client rather than the generated operations: the
 * client is generated per environment, and `/v1/search/configurations` is not on every one yet,
 * so importing its operation would break that environment's build. The module contract types it.
 */
const searchRoute = <T>(route: string, q: string, signal: AbortSignal) =>
  apiMutator<SearchBody<T>>({
    url: `/v1/search/${route}`,
    method: 'GET',
    params: { q, limit: SEARCH_GROUP_LIMIT },
    signal,
  });

/** One route per group, each answering `{total, items}`. */
const SOURCES: Record<SearchGroupType, GroupSource> = {
  DEVICE: {
    permission: 'device.read',
    fetch: async (q, signal) =>
      toGroup('DEVICE', await searchRoute<DeviceSearchItem>('devices', q, signal), toDeviceResult),
  },
  WORKFLOW: {
    permission: 'workflow.read',
    fetch: async (q, signal) =>
      toGroup(
        'WORKFLOW',
        await searchRoute<WorkflowSearchItem>('workflows', q, signal),
        toWorkflowResult,
      ),
  },
  PLAN: {
    permission: 'plan.read',
    fetch: async (q, signal) =>
      toGroup('PLAN', await searchRoute<PlanSearchItem>('plans', q, signal), toPlanResult),
  },
  CONFIGURATION: {
    permission: 'search.configurations',
    fetch: async (q, signal) =>
      toGroup(
        'CONFIGURATION',
        await searchRoute<ConfigurationSearchItem>('configurations', q, signal),
        toConfigurationResult,
      ),
  },
};

/** A group the edge refuses is one the user cannot see: it is left out, not an error. */
const isForbidden = (error: unknown) => isAppError(error) && error.kind === 'forbidden';

/**
 * The global search's results for `query`: one request per group the user may read, sent once the
 * trimmed text reaches `SEARCH_MIN_LENGTH`. Each group is keyed by the text it answers, so a slow
 * answer for older text never lands under newer text. Groups show as they answer, in panel order;
 * the panel errors only when every group failed, so one group's outage does not hide the rest.
 */
export function useGlobalSearchQuery(query: string) {
  const { can } = usePermission();
  const trimmed = query.trim();
  const enabled = trimmed.length >= SEARCH_MIN_LENGTH;
  const types = SEARCH_GROUPS.filter((type) => can(SOURCES[type].permission));

  const results = useQueries({
    queries: types.map((type) => ({
      queryKey: ['search', type, trimmed],
      queryFn: ({ signal }: { signal: AbortSignal }) => SOURCES[type].fetch(trimmed, signal),
      enabled,
      // Keeps the last answer on screen while the next one loads, so the panel does not flash.
      placeholderData: keepPreviousData,
      // Search results go stale as devices change state; the next keystroke asks again anyway.
      staleTime: 0,
      // Silent and kept in place: the panel renders its own error state with the reference id,
      // and a 403 only drops its group rather than replacing the page with the 403 screen.
      throwOnError: false,
      meta: { silent: true },
    })),
  });

  const groups = results.flatMap((result) =>
    result.data && result.data.total > 0 ? [result.data] : [],
  );
  const failures = results.filter((result) => result.isError && !isForbidden(result.error));

  return {
    groups,
    // Loading until the first group answers: the rest fill in under it as they arrive.
    isLoading: enabled && groups.length === 0 && results.some((result) => result.isLoading),
    // Every group failed, and not all of them only for want of permission.
    isError: failures.length > 0 && results.every((result) => result.isError),
    error: failures[0]?.error ?? null,
    refetch: () => Promise.all(failures.map((result) => result.refetch())),
  };
}
